import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { waitForAngularUpdate } from 'services/browser-utils';
import { PubSubService, navigationStackChangedEvent, PubSubRegistration } from 'services/pubsub.service';
import { VirtualKeyboardService } from 'services/virtual-keyboard.service';
import { pickActionButtonsForPage } from '../cs-page-action-buttons/action-buttons-owner';
import { CsScrollableComponent } from '../cs-scrollable/cs-scrollable';
import { ChromePaddingSource, TrackedChromePadding } from './tracked-chrome-padding';

type ChromePaddingField = 'headerPaddingHeight' | 'actionButtonsPaddingHeight' | 'bottomNavPaddingHeight';

@Component({
    selector: 'cs-content',
    templateUrl: 'cs-content.html',
    host: { 'class': 'content' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsContentComponent implements OnDestroy, AfterViewInit, OnChanges {
  @Input() loading: boolean;
  @Input('no-scroll') noScroll: boolean = false;
  showSpinner = false;
  animateContent = false;
  private spinnerDelayTimer: ReturnType<typeof setTimeout> | null = null;
  @Output() scroll = new EventEmitter<Event>();
  @ViewChild('scrollable', { static: false }) scrollable: CsScrollableComponent;

  // Local property for keyboard padding
  keyboardPaddingHeight: number = 0;
  headerPaddingHeight: number = 0;
  actionButtonsPaddingHeight: number = 0;
  bottomNavPaddingHeight: number = 0;
  private isDestroyed = false;
  // one tracker per measured piece of chrome; the keyboard is pushed in instead
  private headerPadding?: TrackedChromePadding;
  private actionButtonsPadding?: TrackedChromePadding;
  private bottomNavPadding?: TrackedChromePadding;
  private headerAdditionObserver?: MutationObserver;
  private currentHeader: Element | null = null;
  private navigationSubscription: PubSubRegistration | null = null;
  private isModalPage = false;
  private isAutoHeightModal = false;
  private pageElement: HTMLElement | null = null;
  private actionButtonsEventHandler?: () => void;

  constructor(
    public keyboardService: VirtualKeyboardService,
    private injector: Injector,
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private pubSub: PubSubService,
    @Optional() private modalMarker: ModalPageMarker
  ) {
    // Register this component with the keyboard service
    this.keyboardService.registerContentComponent(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    // handle spinner delay logic
    if (changes['loading']) {
      if (changes['loading'].currentValue === true) {
        // reset animation flag when loading starts
        this.animateContent = false;
        // start timer to show spinner after delay
        this.spinnerDelayTimer = setTimeout(() => {
          this.showSpinner = true;
          this.cd.markForCheck();
        }, 300);
      } else {
        // loading finished - clear timer and hide spinner
        if (this.spinnerDelayTimer) {
          clearTimeout(this.spinnerDelayTimer);
          this.spinnerDelayTimer = null;
        }
        // only animate if spinner was shown
        this.animateContent = this.showSpinner;
        this.showSpinner = false;
      }
    }
  }

  /**
   * Check if action buttons should be floating (and thus need padding).
   * Buttons float on small screens or on modal pages.
   */
  private shouldActionButtonsFloat(): boolean {
    if (hasClientSmallScreen()) {
      return true;
    }
    // check cached modal status
    if (this.isModalPage) {
      return true;
    }
    return false;
  }

  async ngAfterViewInit() {

    await waitForAngularUpdate(this.injector);
    // the wait now returns when this component is destroyed instead of hanging
    // (df-3f0eaff27ac6), so the observers set up below have to be skipped in that
    // case — ngOnDestroy has already run and disconnected whatever existed then, so
    // anything registered after it is never disconnected at all
    if (this.isDestroyed) return;

    // Walk up the tree to find page boundary
    let element: HTMLElement | null = this.elementRef.nativeElement;
    let pageElement: HTMLElement | null = null;

    // Walk up until we find a page element
    while (element) {
      // if current element is a page (page-* selector)
      const tagName = element.tagName?.toLowerCase();
      if (tagName && tagName.startsWith('page-')) {
        // We've reached a page boundary
        pageElement = element;
        break;
      }
      // Move up to parent
      element = element.parentElement;
    }

    // Store pageElement for later use
    this.pageElement = pageElement;

    // Try to set up header observer (may not exist yet if conditionally rendered)
    this.setupHeaderObserver();

    // if header wasn't found, set up a MutationObserver to detect when it's added
    if (!this.currentHeader && this.pageElement) {
      this.headerAdditionObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // check added nodes for cs-header
          for (const node of Array.from(mutation.addedNodes)) {
            if (node instanceof HTMLElement) {
              if (node.tagName?.toLowerCase() === 'cs-header') {
                this.setupHeaderObserver();
                return;
              }
              // also check children in case header is nested
              if (node.querySelector?.('cs-header')) {
                this.setupHeaderObserver();
                return;
              }
            }
          }
        }
      });
      this.headerAdditionObserver.observe(this.pageElement, { childList: true, subtree: true });
    }

    // if ModalPageMarker is injected, we're in a modal page
    this.isModalPage = this.modalMarker != null;
    this.isAutoHeightModal = this.isModalPage && this.pageElement?.classList.contains('auto-height');
    this.setupActionButtonsObserver();

    // listen for dynamically added action buttons
    if (this.pageElement) {
      this.actionButtonsEventHandler = () => this.refreshActionButtonsPadding();
      this.pageElement.addEventListener('actionButtonsAdded', this.actionButtonsEventHandler);
    }

    // Check for bottom nav (global, one per app)
    // (padding only while it is visible, i.e. below the collapsed menu breakpoint)
    const bottomNav = document.querySelector('cs-bottom-nav') as HTMLElement | null;
    if (bottomNav) {
      this.bottomNavPadding = this.trackChromePadding('bottomNavPaddingHeight', {
        element: bottomNav,
        readHeight: (el) => el.offsetHeight,
        shouldReserve: () => this.isBottomNavVisible(),
        watchClass: false,
      });
    }

    // subscribe to navigation events to refresh bottom nav padding when returning to a page
    // (ResizeObserver only fires on size changes, not when the page becomes visible again)
    this.navigationSubscription = this.pubSub.subscribe(navigationStackChangedEvent, () => {
      requestAnimationFrame(() => {
        this.refreshBottomNavPadding();
      });
    });
  }

  /**
   * Update --modal-chrome-height CSS variable on the page element.
   * This tells the scrollable area how much space is taken by header + action buttons,
   * so it can calculate max-height dynamically instead of using a hardcoded value.
   */
  private updateModalChromeHeight() {
    if (!this.isAutoHeightModal || !this.pageElement) return;
    const header = this.currentHeader as HTMLElement;
    const buttons = this.pageElement.querySelector('cs-page-action-buttons') as HTMLElement;
    const chromeHeight = (header?.offsetHeight || 0) + (buttons?.offsetHeight || 0);
    if (chromeHeight > 0) {
      this.pageElement.style.setProperty('--modal-chrome-height', `${chromeHeight}px`);
    }
  }

  /**
   * Set up observers for header height tracking.
   * Called on init and when header is dynamically added (e.g., via *ngIf).
   */
  private setupHeaderObserver() {
    if (this.isDestroyed) {
      return;
    }

    // if already tracking a header, skip
    if (this.currentHeader) {
      return;
    }

    // find header within page element
    const header = this.pageElement?.querySelector('cs-header');
    if (!header) {
      return;
    }

    this.currentHeader = header;

    // disconnect the addition observer since we found the header
    if (this.headerAdditionObserver) {
      this.headerAdditionObserver.disconnect();
      this.headerAdditionObserver = undefined;
    }

    // Helper to check if header needs scroll padding
    // Padding is only needed when header is fixed (small screen + hide-on-scroll enabled)
    // Fixed-size mobile modals use relative positioning, so no padding needed
    const needsHeaderPadding = () =>
      hasClientSmallScreen()
      && !header.classList.contains('disable-hide-on-scroll')
      && this.modalMarker?.config?.fullScreenOnMobile !== false;

    this.headerPadding = this.trackChromePadding('headerPaddingHeight', {
      element: header as HTMLElement,
      readHeight: (el) => el.offsetHeight || 0,
      shouldReserve: needsHeaderPadding,
      watchClass: true,
      onResize: () => this.updateModalChromeHeight(),
    });
  }

  /** start tracking one piece of chrome whose reserved height lives in `field` */
  private trackChromePadding(
    field: ChromePaddingField,
    source: Pick<ChromePaddingSource, 'element' | 'readHeight' | 'shouldReserve' | 'watchClass' | 'onResize'>,
  ): TrackedChromePadding {
    return new TrackedChromePadding({
      ...source,
      isAlive: () => !this.isDestroyed,
      current: () => this[field],
      publish: (height) => this.setChromePaddingHeight(field, height),
    });
  }

  /** store a new reserved height for one piece of chrome and ask for a redraw */
  private setChromePaddingHeight(field: ChromePaddingField, newHeight: number) {
    // observers can fire after destroy; there is nothing left to update then
    if (this.isDestroyed) return;
    if (this[field] === newHeight) return;
    this[field] = newHeight;
    this.cd.markForCheck();
  }

  /**
   * Set up ResizeObserver for action buttons if they exist.
   * Called both on init and when action buttons are dynamically added.
   */
  private setupActionButtonsObserver() {
    if (!this.pageElement || this.actionButtonsPadding) {
      return;
    }

    // First try to find action buttons within the page element
    let actionButtons: Element | null = this.pageElement.querySelector('cs-page-action-buttons');

    // If not found in the page, look in split-pane-main-wrapper, where a small-screen bar
    // moves itself. That wrapper is shared by every page, so ask for OUR page's bar by
    // owner rather than taking the first one — see action-buttons-owner.ts (#4661).
    // Finding none is the right answer: the `actionButtonsAdded` listener re-runs this
    // lookup when our bar arrives, whereas a wrong match is never revisited.
    if (!actionButtons && hasClientSmallScreen()) {
      const wrapper = document.querySelector('.split-pane-main-wrapper');
      const parkedButtons = wrapper ? Array.from(wrapper.querySelectorAll('cs-page-action-buttons')) : [];
      actionButtons = pickActionButtonsForPage(parkedButtons, this.pageElement);
    }
    if (actionButtons) {
      // visible-aware height reader: when cs-page-action-buttons hides itself by sliding
      // off-screen via transform, offsetHeight still reports the laid-out height. That's
      // wrong padding to reserve for an inactive page (e.g. a parent page whose buttons
      // are parked in .split-pane-main-wrapper while a child page is shown). Treat
      // non-`.visible` buttons as height 0.
      //
      // padding is only reserved while the buttons float (small screens or modal pages);
      // in the header they take no scroll space. The class watcher catches the .visible
      // flip, which a resize watcher misses because sliding in/out is transform-only.
      this.actionButtonsPadding = this.trackChromePadding('actionButtonsPaddingHeight', {
        element: actionButtons as HTMLElement,
        readHeight: (el) => el.classList.contains('visible') ? el.offsetHeight : 0,
        shouldReserve: () => this.shouldActionButtonsFloat(),
        watchClass: true,
        onResize: () => this.updateModalChromeHeight(),
      });
    }
  }

  /**
   * Called by cs-page-action-buttons when it is dynamically added to the page.
   * Re-runs the setup to create the ResizeObserver for the newly added element.
   */
  refreshActionButtonsPadding() {
    if (this.isDestroyed) {
      return;
    }

    // stop tracking the old element, if any
    this.actionButtonsPadding?.dispose();
    this.actionButtonsPadding = undefined;

    // set up observer for the (possibly new) action buttons element
    this.setupActionButtonsObserver();
  }

  /**
   * Refresh bottom nav padding height - called after navigation to ensure padding is correct
   */
  private refreshBottomNavPadding() {
    if (this.isDestroyed) {
      return;
    }

    // look the bottom nav up afresh rather than asking the tracker: app.html rebuilds it
    // when the screen crosses the side-menu breakpoint, so the element the tracker holds
    // may be a detached one that measures 0
    if (!this.isBottomNavVisible()) {
      this.setChromePaddingHeight('bottomNavPaddingHeight', 0);
      return;
    }
    const bottomNav = document.querySelector('cs-bottom-nav') as HTMLElement | null;
    if (bottomNav) {
      this.setChromePaddingHeight('bottomNavPaddingHeight', bottomNav.offsetHeight);
    }
  }

  // Check if bottom nav is visible (via has-bottom-nav class on cs-split-pane)
  private isBottomNavVisible(): boolean {
    const splitPane = document.querySelector('cs-split-pane');
    return splitPane?.classList.contains('has-bottom-nav') ?? false;
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.keyboardService.unregisterContentComponent(this);

    // Clean up spinner delay timer
    if (this.spinnerDelayTimer) {
      clearTimeout(this.spinnerDelayTimer);
    }

    // stop watching the page chrome
    for (const tracker of [this.headerPadding, this.actionButtonsPadding, this.bottomNavPadding]) {
      tracker?.dispose();
    }
    if (this.headerAdditionObserver) {
      this.headerAdditionObserver.disconnect();
    }

    // clean up navigation subscription
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }

    // clean up action buttons event listener
    if (this.pageElement && this.actionButtonsEventHandler) {
      this.pageElement.removeEventListener('actionButtonsAdded', this.actionButtonsEventHandler);
    }
  }

  handleScroll(event: Event) {
    this.scroll.emit(event);
  }

  // Method to scroll to top if needed
  scrollToTop() {
    if (this.scrollable) {
      this.scrollable.resetToTop();
    }
  }

  // the element that scrolls this page's content, for use as an IntersectionObserver root
  getScrollRootElement(): HTMLElement | null {
    return this.scrollable?.getScrollRootElement() ?? null;
  }

  // Setter for keyboard padding height - called by VirtualKeyboardService
  setContentPaddingForKeyboard(height: number) {
    // Check if component is destroyed to avoid NG0911 error
    if (this.isDestroyed) {
      return;
    }
    if (!this.scrollable) {
      return; // for some reason, waitForAngularUpdate() never finishes if there's no scrollable
    }
    this.keyboardPaddingHeight = height;
    // Trigger immediate change detection to update DOM synchronously
    this.cd.detectChanges();
  }
}